import { cloneElement, isValidElement, type ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";

export interface Crumb { label: string; to?: string }

export function PageHeader({ title, subtitle, crumbs, actions }: { title: ReactNode; subtitle?: ReactNode; crumbs?: Crumb[]; actions?: ReactNode }) {
  return (
    <div className="page-head">
      <div className="page-head-text">
        {crumbs?.length ? (
          <nav className="crumbs" aria-label="Breadcrumb">
            {crumbs.map((c, i) => (
              <span key={i} className="row" style={{ gap: 8 }}>
                {c.to ? <Link to={c.to}>{c.label}</Link> : <span aria-current="page">{c.label}</span>}
                {i < crumbs.length - 1 ? <span aria-hidden="true">›</span> : null}
              </span>
            ))}
          </nav>
        ) : null}
        <h1 className="page-title">{title}</h1>
        {subtitle ? <p className="page-sub">{subtitle}</p> : null}
      </div>
      {actions ? <div className="page-actions">{actions}</div> : null}
    </div>
  );
}

export function Panel({ title, actions, children, flush, id }: { title?: ReactNode; actions?: ReactNode; children: ReactNode; flush?: boolean; id?: string }) {
  return (
    <section className="panel" aria-labelledby={id}>
      {title ? (
        <div className="panel-head">
          <h2 className="panel-title" id={id}>{title}</h2>
          {actions}
        </div>
      ) : null}
      <div className={`panel-body${flush ? " flush" : ""}`}>{children}</div>
    </section>
  );
}

export interface TabDef { to: string; label: string; count?: number | null; end?: boolean }

/** Tabs that are links, so each tab has its own address. */
export function LinkTabs({ tabs, label }: { tabs: TabDef[]; label: string }) {
  return (
    <nav className="tabs" aria-label={label}>
      {tabs.map((t) => (
        <NavLink key={t.to} to={t.to} end={t.end} className={({ isActive }) => `tab${isActive ? " active" : ""}`}>
          {t.label}
          {t.count !== undefined && t.count !== null ? <span className="count">{t.count}</span> : null}
        </NavLink>
      ))}
    </nav>
  );
}

export function Segmented<T extends string>({ value, options, onChange, label }: { value: T; options: { value: T; label: string }[]; onChange: (v: T) => void; label: string }) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" aria-pressed={o.value === value} onClick={() => onChange(o.value)}>{o.label}</button>
      ))}
    </div>
  );
}

/** The hint's id, for a control wrapped in another element: pass it as the control's aria-describedby. */
export const fieldHintId = (htmlFor: string) => `${htmlFor}-hint`;

export function Field({ label, hint, error, children, htmlFor, grow, liveHint }: {
  label: string; hint?: string; error?: string | null; children: ReactNode; htmlFor: string; grow?: boolean;
  /** Read the hint out when it changes (e.g. a Caps Lock warning). The hint area then always exists. */
  liveHint?: boolean;
}) {
  const id = fieldHintId(htmlFor);
  const note = error || hint;
  // A control passed directly is tied to the hint or error, so screen readers read it with the field.
  const control = note && isValidElement<{ id?: string; "aria-describedby"?: string }>(children) && children.props.id === htmlFor
    ? cloneElement(children, { "aria-describedby": [children.props["aria-describedby"], id].filter(Boolean).join(" ") })
    : children;
  return (
    <div className={`field${grow ? " grow" : ""}`}>
      <label className="field-label" htmlFor={htmlFor}>{label}</label>
      {control}
      {error ? <span className="field-error" id={id} role="alert">{error}</span>
        : liveHint ? <span className="field-hint" id={id} aria-live="polite">{hint ?? ""}</span>
        : hint ? <span className="field-hint" id={id}>{hint}</span> : null}
    </div>
  );
}
